/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { useEffect, type MouseEvent, type ReactNode } from 'react';
import { Button } from 'reactstrap';
import { useOktaAuth } from '@okta/okta-react';
import { Footer } from '@g17eco/molecules/footer';
import Logo from '../../images/logo.svg';
import Icon from '../../images/g17eco-icon.svg';
import { logout } from '../../actions/auth';
import { useAppDispatch } from '../../reducers';
import { useFindEnvironmentQuery, type UserEnvironment } from '@api/users';
import { homepageViews } from '../home/flavours/config';
import '../not-activated/styles.scss';
import './styles.scss';

const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return 'Could not load your environments.';
};

const getHost = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

const RedirectLayout = ({ children }: { children: ReactNode }) => (
  <div className='loginRedirect'>
    <main className='loginRedirectBody'>{children}</main>
    <Footer />
  </div>
);

const getFlag = (url: string) => {
  const host = getHost(url);
  return homepageViews.find((view) => getHost(view.url) === host)?.flag;
};

const HostPill = ({ url }: { url: string }) => {
  const flag = getFlag(url);
  return (
    <span className='loginRedirectHost'>
      {flag ? (
        <img className='loginRedirectFlag' src={flag} alt='' />
      ) : (
        <i className='fa-light fa-globe' aria-hidden='true' />
      )}
      {getHost(url)}
    </span>
  );
};

const FindingWorkspace = () => (
  <RedirectLayout>
    <div className='loginRedirectSpinner' role='status' aria-label='Loading'>
      <img className='loginRedirectIcon' src={Icon} alt='' />
    </div>
    <h1>Finding your workspace…</h1>
    <p>Checking which G17Eco region your account is in</p>
  </RedirectLayout>
);

const Redirecting = ({ environment }: { environment: UserEnvironment }) => (
  <RedirectLayout>
    <img className='loginRedirectIcon' src={Icon} alt='' />
    <p className='loginRedirectEyebrow'>Taking you to</p>
    <h1>G17Eco {environment.label}</h1>
    <HostPill url={environment.url} />
    <div className='loginRedirectProgress' role='progressbar' aria-label='Redirecting' />
    <p className='loginRedirectContinue'>
      Not redirected? <a href={environment.url}>Continue to {getHost(environment.url)}</a>
    </p>
  </RedirectLayout>
);

const ChooseWorkspace = ({ environments }: { environments: UserEnvironment[] }) => (
  <RedirectLayout>
    <img className='loginRedirectIcon' src={Icon} alt='' />
    <h1>Choose your workspace</h1>
    <p>Your account has access in more than one region</p>
    <ul className='loginRedirectList'>
      {environments.map((env) => (
        <li key={env.region}>
          <a href={env.url}>
            <span className='loginRedirectLabel'>G17Eco {env.label}</span>
            <HostPill url={env.url} />
            <i className='fal fa-chevron-right' aria-hidden='true' />
          </a>
        </li>
      ))}
    </ul>
  </RedirectLayout>
);

const CardLayout = ({ children }: { children: ReactNode }) => (
  <div className='notActivatedWrapper'>
    <div className='notActivatedContainer'>
      <header>
        <img className='logo' src={Logo} alt='G17Eco' />
      </header>
      <div className='notActivatedBodyContainer'>{children}</div>
      <Footer />
    </div>
  </div>
);

export const LoginRedirectRoute = () => {
  const { authState } = useOktaAuth();
  const isAuthenticated = !!authState?.isAuthenticated;
  const { data, isError, error } = useFindEnvironmentQuery(undefined, {
    skip: !isAuthenticated,
  });
  const dispatch = useAppDispatch();
  const environments = data?.environments ?? [];
  const hasLocalAccess = data?.hasLocalAccess ?? false;

  useEffect(() => {
    if (authState && !authState.isAuthenticated) {
      window.location.href = '/';
    }
  }, [authState]);

  useEffect(() => {
    if (data?.hasLocalAccess) {
      window.location.href = '/';
      return;
    }
    if (data?.environments.length === 1) {
      window.location.href = data.environments[0].url;
    }
  }, [data]);

  const handleLogout = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    dispatch(logout());
  };

  if (!isAuthenticated || hasLocalAccess) {
    return <FindingWorkspace />;
  }
  if (isError) {
    return (
      <CardLayout>
        <h1>Something went wrong</h1>
        <h3>{getErrorMessage(error)}</h3>
        <p>
          <Button color='primary' onClick={() => window.location.reload()}>Try again</Button>
          <Button color='link' onClick={handleLogout}>Logout</Button>
        </p>
      </CardLayout>
    );
  }
  if (!data) {
    return <FindingWorkspace />;
  }
  const [first, ...rest] = environments;
  if (!first) {
    return (
      <CardLayout>
        <h1>No access found</h1>
        <h3>Your account isn't associated with any environment yet.</h3>
        <p>
          Please contact your manager or admin to be added, or
          <Button color='link' size='sm' onClick={handleLogout}>click here to logout</Button>
          and try a different account.
        </p>
      </CardLayout>
    );
  }
  return rest.length === 0 ? <Redirecting environment={first} /> : <ChooseWorkspace environments={environments} />;
};
